import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoIcon from '@mui/icons-material/Info';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import MobileStepper from '@mui/material/MobileStepper';
import PopoverMui from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SwipeableViews from 'react-swipeable-views';
import Chip from '/@/components/bayer/chip';
import FileDropper from '/@/components/file-dropper';
import Popover from '/@/components/popover';
import colors from '/@/components/theme-provider/colors';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { getAndSetCropMonitoringOnNitrogenSmartRedux } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { useNitrogenSources } from '/@/hooks/nitrogen-smart';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { MuiSelect } from '/@/mui/select';
import { getActiveBrands } from '/@/store/bag-quality/selectors';
import {
    addManagementZone,
    removeManagementZone,
    setApplicationDate,
    setEstimatedApplicationDate,
    setNewManagementZoneFiles,
    setNitrogenSource,
    setPlantingDate,
    setProductName,
    setSelectedHybrid,
    setType,
} from '/@/store/nitrogen-recommendation/actions';
import {
    getApplicationDate,
    getCropMonitoring,
    getEstimatedApplicationDate,
    getManagementZoneFile,
    getManagementZones,
    getNitrogenSource,
    getPlantingDate,
    getSelectedHybrid,
    getType,
} from '/@/store/nitrogen-recommendation/selectors';
import { getValidPlantingDates } from '/@/store/private-data/recommendations/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import { createPlantingDateValidator } from '/@/utils/date';
import useIsMobile from '/@/utils/hooks/is-mobile';
import ManagementZone from '../management-zone';
import FirstImage from './assets/first-image.svg';
import SecondImage from './assets/second-image.svg';
import ThirdImage from './assets/third-image.svg';

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography variant='subtitle1' color='primary' sx={{ fontWeight: 600 }}>
        {children}
    </Typography>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography variant='body2' color='primary' sx={{ fontWeight: 600 }}>
        {children}
    </Typography>
);

const FieldHelper = ({ children }: { children: React.ReactNode }) => (
    <Typography variant='caption' color='primary.mui.inactive'>
        {children}
    </Typography>
);

// `medium` to match the section's MuiSelect fields. The size must live here, not as a
// top-level `size` prop on MuiDatePicker — the wrapper spreads slotProps.textField last,
// so a top-level size is always overridden by this value.
const datePickerSlotProps = (placeholder: string) => ({
    textField: { placeholder, fullWidth: true, size: 'medium' as const },
});

const useFieldMinMaxEnvironment = (field) => {
    return useMemo(() => {
        if (!field)
            return {
                min: null,
                max: null,
            };
        return field.node.zones.reduce(
            (result, { minEnvironment, maxEnvironment }) => {
                if (minEnvironment && minEnvironment <= (result.min || minEnvironment)) result.min = minEnvironment;
                if (maxEnvironment && maxEnvironment >= (result.max || maxEnvironment)) result.max = maxEnvironment;

                return result;
            },
            {
                min: null,
                max: null,
            }
        );
    }, [field]);
};

// `section` chooses which slice of the N-Smart form this renders:
// - 'siembra'  → Configuración general + Refertilización (tipo de dosis)
// - 'ambiente' → management zones (fija/variable) + polygon importer
const FieldStepper = ({ selected, edit, section = 'siembra' }) => {
    const managementZoneFiles = useSelector(getManagementZoneFile);
    const fieldQuery = useFieldById({ id: selected });

    const dispatch = useDispatch();
    const type = useSelector(getType);
    const plantingDate = useSelector(getPlantingDate);
    const estimatedApplicationDate = useSelector(getEstimatedApplicationDate);
    const applicationDate = useSelector(getApplicationDate);
    const hybrid = useSelector(getSelectedHybrid);
    const country = useSelector(getSessionCountry);
    const cornBrands = useSelector((state) => getActiveBrands(state, 'corn'));

    const productsQuery = useAllCatalogProducts({
        country: country.code,
        brand: cornBrands?.[0].name,
        crop: 'corn',
    });

    const cropMonitoring = useSelector(getCropMonitoring);

    const nitrogenSource = useSelector(getNitrogenSource);
    const { data: nitrogenSources } = useNitrogenSources();
    const qparams = new URLSearchParams(window.location.search);

    const minMaxEnvironment = useFieldMinMaxEnvironment(fieldQuery.data);
    const managementZones = useSelector(getManagementZones);

    // Lock "Fecha de siembra" to the corn planting windows declared on the field's zones,
    // matching the corn recommendation / crop-monitoring flows. Test ±1 year so the window
    // is valid regardless of which side of the year boundary today falls on.
    const validPlantingDates = useMemo(
        () => getValidPlantingDates({ crop: 'corn' }, fieldQuery.data),
        [fieldQuery.data]
    );
    const shouldDisablePlantingDate = useMemo(
        () => createPlantingDateValidator(validPlantingDates, [-1, 0, 1]),
        [validPlantingDates]
    );

    // Refertilización happens ~60 days after sowing, so lock both refertilización max date
    // to the same zone windows as "Fecha de siembra" max date shifted forward by 60 days.
    const validRefertilizationDates = useMemo(
        () =>
            validPlantingDates?.map(([min, max]) => [min, dayjs(max).add(60, 'day').format('YYYY-MM-DD')]) as
                | [string, string][]
                | undefined,
        [validPlantingDates]
    );
    const shouldDisableRefertilizationDate = useMemo(
        () => createPlantingDateValidator(validRefertilizationDates, [-1, 0, 1]),
        [validRefertilizationDates]
    );

    const [activeStep, setActiveStep] = React.useState(0);

    const productOptions = useMemo(() => {
        if (!productsQuery.data) return [];
        const allProducts = productsQuery.data?.results;
        return uniqBy(allProducts, 'catalogName').map((el) => ({
            label: el.catalogName,
            value: el.productId,
        }));
    }, [productsQuery.data]);

    const hybridOptions = useMemo(
        () => productOptions.map((o) => ({ label: o.label, value: String(o.value) })),
        [productOptions]
    );
    const sourceOptions = useMemo(
        () => (nitrogenSources || []).map((o) => ({ label: o.label, value: String(o.value) })),
        [nitrogenSources]
    );

    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    // Files handler
    const [files, setFiles] = useState(managementZoneFiles.length ? managementZoneFiles : []);
    const requiredExtensions = useMemo(() => [['.shx', '.shp', '.dbf']], []);
    useEffect(() => {
        dispatch(setNewManagementZoneFiles(files));
    }, [files]);

    useEffect(() => {
        if (qparams.get('id')) {
            const recommendationFiles = managementZoneFiles.map((item) => {
                const filename = item.file.split('/').pop();
                return { ...item, name: filename };
            });
            setFiles(recommendationFiles);
        }
    }, []);

    const createManagementZone = () => {
        if (managementZones.length === 0) {
            dispatch(addManagementZone({ type, id: 1 }));
        }
    };

    const helpText = [
        { title: _('1. Busca los 3 archivos necesarios.'), image: FirstImage },
        { title: _('2. Deben tener el mismo nombre.'), image: SecondImage },
        { title: _('3. Arrastrarlos o seleccionarlos.'), image: ThirdImage },
    ];

    useEffect(() => {
        createManagementZone();
    }, [type]);

    // The field's active crop monitoring, but only while it's still useful for a
    // refertilization recommendation (before V6); null otherwise.
    const validCropMonitoring = useMemo(() => {
        const activeCropMonitoring = fieldQuery.data?.activeCropMonitoring;
        const vegetativeStage = getCropMonitoringData(activeCropMonitoring)?.state?.vegetativeStage;
        return vegetativeStage && vegetativeStage < 6 ? activeCropMonitoring : null;
    }, [fieldQuery.data]);

    const [{ open, anchorEl }, { handleClick, handleClose }] = useAnchorEl();
    const id = open ? 'simple-popover' : undefined;

    const handleDeleteManagementZone = (type, id) => {
        dispatch(removeManagementZone({ type, id }));
    };

    const handleNewManagementZone = (type) => {
        const id = managementZones[managementZones.length - 1].id + 1;
        dispatch(addManagementZone({ type, id }));
    };

    const isMobile = useIsMobile();

    // When the field has a valid active crop monitoring, autocomplete the form directly from it
    // instead of prompting the user to pick it. Create flow only — in edit mode the parent
    // already loads the saved recommendation data, which we must not overwrite. The ref guards
    // against re-applying (and clobbering the user's edits) on background SWR revalidations.
    const autoFilledCmId = useRef<number | null>(null);
    useEffect(() => {
        if (edit || !validCropMonitoring || autoFilledCmId.current === validCropMonitoring.id) return;
        autoFilledCmId.current = validCropMonitoring.id;
        getAndSetCropMonitoringOnNitrogenSmartRedux(dispatch, validCropMonitoring.id);
    }, [edit, validCropMonitoring, dispatch]);

    const handleHybridChange = (e) => {
        const value = e.target.value;
        const opt = productOptions.find((o) => String(o.value) === String(value));
        dispatch(setSelectedHybrid(opt ? opt.value : value));
        dispatch(setProductName(opt ? opt.label : ''));
    };

    const handleSourceChange = (e) => {
        const value = e.target.value;
        const opt = (nitrogenSources || []).find((o) => String(o.value) === String(value));
        dispatch(setNitrogenSource(opt ? opt.value : value));
    };

    if (section === 'siembra') {
        return (
            <Stack spacing={4}>
                <Stack spacing={2}>
                    <SectionTitle>{_('Configuración general')}</SectionTitle>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 3,
                        }}
                    >
                        <Stack spacing={2}>
                            <FieldLabel>{_('Fecha de siembra')}</FieldLabel>
                            <MuiDatePicker
                                value={plantingDate ? dayjs(plantingDate) : null}
                                onChange={(v) => dispatch(setPlantingDate(v ? v.format('YYYY-MM-DD') : undefined))}
                                shouldDisableDate={shouldDisablePlantingDate}
                                slotProps={datePickerSlotProps(_('Seleccionar una fecha'))}
                            />
                        </Stack>

                        <Stack spacing={2}>
                            <FieldLabel>{_('Híbrido')}</FieldLabel>
                            <MuiSelect
                                placeholder={_('Elegir un producto')}
                                formControlProps={{ fullWidth: true }}
                                value={hybrid !== undefined && hybrid !== null ? String(hybrid) : ''}
                                options={hybridOptions}
                                onChange={handleHybridChange}
                            />
                        </Stack>

                        {!edit ? (
                            <Stack spacing={2}>
                                <Stack direction='row' alignItems='center' spacing={0.5} justifyContent='space-between'>
                                    <FieldLabel>{_('Fecha de refertilización estimada')}</FieldLabel>
                                    <Tooltip
                                        arrow
                                        title={
                                            <Typography variant='body2' color='white'>
                                                {_(
                                                    'Ingresá una fecha estimada. Podés editarla hasta 4 días antes; después se bloquea.'
                                                )}
                                            </Typography>
                                        }
                                    >
                                        <InfoIcon sx={{ color: 'primary.mui.inactive', fontSize: '16px' }} />
                                    </Tooltip>
                                </Stack>
                                <MuiDatePicker
                                    value={estimatedApplicationDate ? dayjs(estimatedApplicationDate) : null}
                                    onChange={(v) =>
                                        dispatch(setEstimatedApplicationDate(v ? v.format('YYYY-MM-DD') : undefined))
                                    }
                                    shouldDisableDate={shouldDisableRefertilizationDate}
                                    slotProps={datePickerSlotProps(_('Seleccionar una fecha'))}
                                />
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <FieldLabel>{_('Fecha de refertilización definitiva')}</FieldLabel>
                                <MuiDatePicker
                                    value={applicationDate ? dayjs(applicationDate) : null}
                                    onChange={(v) =>
                                        dispatch(setApplicationDate(v ? v.format('YYYY-MM-DD') : undefined))
                                    }
                                    shouldDisableDate={shouldDisableRefertilizationDate}
                                    slotProps={datePickerSlotProps(_('Seleccionar una fecha'))}
                                />
                            </Stack>
                        )}

                        <Stack spacing={2}>
                            <FieldLabel>{_('Fuente de nitrógeno')}</FieldLabel>
                            <MuiSelect
                                placeholder={_('Elegir un producto')}
                                formControlProps={{ fullWidth: true }}
                                value={
                                    nitrogenSource !== undefined && nitrogenSource !== null
                                        ? String(nitrogenSource)
                                        : ''
                                }
                                options={sourceOptions}
                                onChange={handleSourceChange}
                            />
                            <FieldHelper>{_('Fuente para aplicar en la re-fertilización.')}</FieldHelper>
                        </Stack>
                    </Box>
                    <Stack spacing={3}>
                        <Stack spacing={0.5}>
                            <SectionTitle>{_('Refertilización')}</SectionTitle>
                            <Typography variant='body2' color='primary.mui.inactive'>
                                {_('Seleccioná el tipo de recomendación que querés recibir')}
                            </Typography>
                        </Stack>
                        <Stack direction='row' spacing={2}>
                            <Chip
                                label={_('Dosis fija')}
                                selected={type === 'fixed'}
                                onClick={() => dispatch(setType('fixed'))}
                            />
                            <Chip
                                label={_('Dosis variable')}
                                selected={type === 'variable'}
                                onClick={() => dispatch(setType('variable'))}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        );
    }

    // section === 'ambiente'
    return (
        <Stack spacing={3}>
            {type === 'variable' && <SectionTitle>{_('Zonas de manejo')}</SectionTitle>}

            {type === 'variable'
                ? managementZones.map((mz, index) => {
                      const zoneId = mz.id || index + 1;
                      return (
                          <Stack key={mz.id} spacing={2}>
                              <Stack direction='row' alignItems='center' justifyContent='space-between'>
                                  <Typography
                                      variant='overline'
                                      color='primary'
                                      sx={{ fontWeight: 500, letterSpacing: '1.75px', lineHeight: '16px' }}
                                  >
                                      {_('Zona de manejo')} {zoneId}
                                  </Typography>
                                  {managementZones.length > 1 && (
                                      <IconButton
                                          size='small'
                                          color='primary'
                                          aria-label={_('Borrar zona')}
                                          onClick={() => handleDeleteManagementZone(mz.type, zoneId)}
                                      >
                                          <DeleteOutlineIcon fontSize='small' />
                                      </IconButton>
                                  )}
                              </Stack>
                              <ManagementZone
                                  type={type}
                                  id={zoneId}
                                  minEnvironment={minMaxEnvironment.min}
                                  maxEnvironment={minMaxEnvironment.max}
                                  edit={false}
                                  cropMonitoring={mz.id === 1 ? cropMonitoring : {}}
                              />
                              <Divider sx={{ borderColor: 'rgba(16, 56, 79, 0.12)' }} />
                          </Stack>
                      );
                  })
                : managementZones.map((mz, index) => (
                      <ManagementZone
                          key={mz.id}
                          type={type}
                          id={mz.id || index + 1}
                          minEnvironment={minMaxEnvironment.min}
                          maxEnvironment={minMaxEnvironment.max}
                          edit={false}
                          cropMonitoring={cropMonitoring}
                      />
                  ))}

            {type === 'variable' && (
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                    <Button
                        variant='text'
                        size='small'
                        startIcon={<AddIcon />}
                        onClick={() => handleNewManagementZone(type)}
                    >
                        {_('Agregar otra zona')}
                    </Button>
                </Box>
            )}

            {type === 'variable' && !edit && (
                <Stack spacing={2}>
                    <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                        <Stack spacing={0.5}>
                            <SectionTitle>{_('Importá el polígono del lote')}</SectionTitle>
                            <FieldHelper>{_('Lo usamos para generar la prescripción.')}</FieldHelper>
                        </Stack>
                        {isMobile ? (
                            <IconButton color='gray' size='small' onClick={handleClick}>
                                <QuestionMarkIcon />
                            </IconButton>
                        ) : (
                            <Button variant='outlined' onClick={handleClick}>
                                {_('¿Necesitas ayuda para cargar archivos?')}
                            </Button>
                        )}

                        <Popover
                            open={open && isMobile}
                            handleClose={handleClose}
                            title={_('Instructivo para carga de archivos')}
                        >
                            <Box sx={{ maxWidth: 300, flexGrow: 1, m: 'auto' }}>
                                <SwipeableViews
                                    axis='x'
                                    index={activeStep}
                                    onChangeIndex={handleStepChange}
                                    enableMouseEvents
                                >
                                    {helpText.map((step, index) => (
                                        <div key={step.title}>
                                            {Math.abs(activeStep - index) <= 2 ? (
                                                <Box
                                                    component='img'
                                                    sx={{
                                                        height: 255,
                                                        display: 'block',
                                                        maxWidth: 400,
                                                        overflow: 'hidden',
                                                        width: '100%',
                                                    }}
                                                    src={step.image}
                                                    alt={step.title}
                                                />
                                            ) : null}
                                        </div>
                                    ))}
                                </SwipeableViews>
                                <MobileStepper
                                    steps={helpText.length}
                                    position='static'
                                    activeStep={activeStep}
                                    sx={{ justifyContent: 'center' }}
                                />
                            </Box>
                        </Popover>

                        <PopoverMui
                            id={id}
                            open={open && !isMobile}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            PaperProps={{
                                style: { padding: 15, display: 'flex', flexDirection: 'column', marginTop: 15 },
                            }}
                        >
                            <Stack spacing={2}>
                                <SectionTitle>{_('Instructivo para carga de archivos')}</SectionTitle>
                                <Stack direction='row' justifyContent='space-around' spacing={2}>
                                    {helpText.map((ht) => (
                                        <Stack key={ht.title} spacing={1} sx={{ width: '30%' }}>
                                            <Typography variant='body2' color='primary'>
                                                {_(ht.title)}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    backgroundColor: colors.gray[200],
                                                    height: 150,
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <img style={{ width: '75%' }} src={ht.image} alt={ht.title} />
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Stack>
                        </PopoverMui>
                    </Stack>
                    <FileDropper
                        requiredExtensions={requiredExtensions}
                        files={files}
                        setFiles={setFiles}
                        description={_('Necesitás los 3 archivos (.shp, .shx, .dbf) con el mismo nombre.')}
                    />
                </Stack>
            )}
        </Stack>
    );
};

export default FieldStepper;
